/**
 * "Logging as" — the shared-device switcher, POS style.
 *
 * design/PROTOTYPE-SPEC.md §14: "a tap on your name + a per-user 4-digit PIN
 * instead of a full password login, so counter logging stays fast while
 * attribution stays real."
 *
 * Deliberately keypad-first rather than a text field — this is used on a tablet
 * with gloves on, at a counter, between clients. A keyboard works too.
 */
import { useEffect, useState } from 'react';
import { JOB_TITLE_LABEL, type JobTitle as CoreJobTitle } from '@inventory-tracker/core';
import {
  ApiError,
  PinLockedError,
  getSwitchable,
  pinSwitch,
  type SwitchableUser,
} from '../lib/api.js';
import { c, sans } from '../theme.js';

interface Props {
  currentName: string;
  onClose: () => void;
  /** Called after a successful switch, so the app re-reads /auth/me. */
  onSwitched: () => void;
}

export function QuickSwitch({ currentName, onClose, onSwitched }: Props) {
  const [users, setUsers] = useState<SwitchableUser[] | null>(null);
  const [picked, setPicked] = useState<SwitchableUser | null>(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getSwitchable()
      .then((r) => setUsers(r.users.filter((u) => !u.isCurrent)))
      .catch(() => setError('Could not load the team'));
  }, []);

  async function submit(value: string) {
    if (!picked || busy) return;
    setBusy(true);
    setError('');
    try {
      await pinSwitch(picked.id, value);
      onSwitched();
    } catch (err) {
      setPin('');
      if (err instanceof PinLockedError) {
        setError(err.message);
      } else {
        setError(err instanceof ApiError ? err.message : 'That did not work');
      }
    } finally {
      setBusy(false);
    }
  }

  function press(digit: string) {
    if (pin.length >= 6) return;
    // No submit on the fourth digit. PINs are 4 to 6 digits and the keypad cannot
    // know which, so auto-submitting sent the first four digits of a longer PIN,
    // failed, and spent one of the five attempts before lockout — a 5 or 6-digit
    // PIN could never be entered at all. The Switch button below confirms.
    setPin(pin + digit);
  }

  const canSubmit = pin.length >= 4 && !busy;

  // The keypad is for the tablet; at a desk people reach for the number row.
  useEffect(() => {
    if (!picked) return;
    function onKey(e: KeyboardEvent) {
      if (busy) return;
      if (/^\d$/.test(e.key)) setPin((p) => (p.length >= 6 ? p : p + e.key));
      else if (e.key === 'Backspace') setPin((p) => p.slice(0, -1));
      else if (e.key === 'Enter' && pin.length >= 4) void submit(pin);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [picked, pin, busy]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Switch user"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20,26,22,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        zIndex: 80,
        fontFamily: sans,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 380,
          maxWidth: '100%',
          background: c.authBg,
          borderRadius: 22,
          boxShadow: '0 30px 70px rgba(20,26,22,0.4)',
          padding: 28,
        }}
      >
        {!picked ? (
          <>
            <h2
              style={{
                fontFamily: sans, letterSpacing: '-0.03em',
                fontWeight: 300,
                fontSize: 26,
                margin: '0 0 4px',
                color: c.ink,
              }}
            >
              Who’s at the counter?
            </h2>
            <p style={{ fontSize: 13, color: c.muted, margin: '0 0 20px' }}>
              Currently logging as {currentName}.
            </p>

            {users === null && <p style={{ fontSize: 13, color: c.muted }}>Loading…</p>}

            {users?.length === 0 && (
              <p style={{ fontSize: 13, color: c.muted, lineHeight: 1.6, margin: 0 }}>
                Nobody else has set a PIN yet. Each person can set one from the Team page, and then
                they’ll appear here.
              </p>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {users?.map((u) => {
                const locked = Boolean(u.lockedUntil);
                return (
                  <button
                    key={u.id}
                    type="button"
                    disabled={locked}
                    onClick={() => {
                      setPicked(u);
                      setPin('');
                      setError('');
                    }}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 2,
                      background: c.white,
                      border: `1px solid ${c.borderStrong}`,
                      borderRadius: 14,
                      padding: '13px 16px',
                      cursor: locked ? 'not-allowed' : 'pointer',
                      opacity: locked ? 0.5 : 1,
                      fontFamily: sans,
                      textAlign: 'left',
                    }}
                  >
                    <span style={{ fontSize: 15, fontWeight: 500, color: c.ink }}>{u.name}</span>
                    <span style={{ fontSize: 12, color: c.muted }}>
                      {JOB_TITLE_LABEL[u.jobTitle as CoreJobTitle]}
                      {locked && ' · PIN locked'}
                    </span>
                  </button>
                );
              })}
            </div>

            {error && <p style={{ fontSize: 13, color: c.danger, margin: '14px 0 0' }}>{error}</p>}

            <button
              type="button"
              onClick={onClose}
              style={{
                marginTop: 18,
                background: 'none',
                border: 'none',
                padding: 0,
                fontSize: 13,
                fontFamily: sans,
                color: c.green,
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <h2
              style={{
                fontFamily: sans, letterSpacing: '-0.03em',
                fontWeight: 300,
                fontSize: 26,
                margin: '0 0 4px',
                color: c.ink,
              }}
            >
              {picked.name}
            </h2>
            <p style={{ fontSize: 13, color: c.muted, margin: '0 0 18px' }}>Enter your PIN.</p>

            {/* Dots rather than the digits: this is a screen other people can see. */}
            {/* One box that fills as you type, not a row of four empty dots: a
                fixed row of four tells someone with a 6-digit PIN it is wrong. */}
            <div
              aria-label={`PIN, ${pin.length} digits entered`}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                height: 46,
                marginBottom: 18,
                background: c.white,
                border: `1px solid ${c.borderStrong}`,
                borderRadius: 12,
              }}
            >
              {pin.length === 0 ? (
                <span style={{ fontSize: 13, color: c.faint }}>4 to 6 digits</span>
              ) : (
                Array.from({ length: pin.length }).map((_, i) => (
                  <span
                    key={i}
                    style={{ width: 11, height: 11, borderRadius: '50%', background: c.ink }}
                  />
                ))
              )}
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 10,
                marginBottom: 14,
              }}
            >
              {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                <Key key={d} onClick={() => press(d)} disabled={busy}>
                  {d}
                </Key>
              ))}
              <Key onClick={() => setPin('')} disabled={busy}>
                Clear
              </Key>
              <Key onClick={() => press('0')} disabled={busy}>
                0
              </Key>
              <Key onClick={() => setPin(pin.slice(0, -1))} disabled={busy}>
                ←
              </Key>
            </div>

            {/* PINs are 4 to 6 digits, so an explicit confirm is needed. Always
                present, so the keypad does not jump when the fourth digit lands. */}
            <button
              type="button"
              onClick={() => void submit(pin)}
              disabled={!canSubmit}
              style={{
                width: '100%',
                height: 46,
                background: c.green,
                border: 'none',
                color: c.onDarkBright,
                fontSize: 14.5,
                fontWeight: 500,
                fontFamily: sans,
                borderRadius: 980,
                cursor: canSubmit ? 'pointer' : 'default',
                opacity: canSubmit ? 1 : 0.4,
                marginBottom: 12,
              }}
            >
              {busy ? 'Switching…' : 'Switch'}
            </button>

            {error && (
              <div
                role="alert"
                style={{
                  background: c.dangerBg,
                  border: `1px solid ${c.dangerBorder}`,
                  color: c.danger,
                  borderRadius: 12,
                  padding: '10px 13px',
                  fontSize: 13,
                  marginBottom: 12,
                  lineHeight: 1.5,
                }}
              >
                {error}
              </div>
            )}

            <button
              type="button"
              onClick={() => {
                setPicked(null);
                setPin('');
                setError('');
              }}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                fontSize: 13,
                fontFamily: sans,
                color: c.green,
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              ← Someone else
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function Key({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        height: 52,
        background: c.white,
        border: `1px solid ${c.borderStrong}`,
        borderRadius: 980,
        fontSize: 19,
        fontWeight: 500,
        fontFamily: sans,
        color: c.ink,
        cursor: disabled ? 'progress' : 'pointer',
      }}
    >
      {children}
    </button>
  );
}
